// ownerreports.js
import '../scss/components/_ownerreports.scss'
import React, { useState, useEffect, useRef } from 'react'
import OwnerOccupancyReport from './OwnerOccupancyReport'
import OwnerRevenueReport from './OwnerRevenueReport'
import OwnerComplaintsReport from './OwnerComplaintsReport'
import OwnerComplianceReport from './OwnerComplianceReport'
import { getOccupancyReport } from '../apis/services/pgApi'

// ─── Constants ───────────────────────────────────────────────────────────────

const REPORT_TYPES = [
  { key: 'occupancy',  label: 'Occupancy Report'  },
  { key: 'revenue',    label: 'Revenue Report'     },
  { key: 'complaints', label: 'Complaints Report'  },
  { key: 'compliance', label: 'Compliance Report'  },
]

// ─── Root Component ──────────────────────────────────────────────────────────

const OwnerReports = ({ selectedPG, selectedPGId }) => {
  const today = new Date()
  const fmt = d => d.toISOString().split('T')[0]

  const [dateFrom, setDateFrom] = useState(fmt(today))
  const [dateTo, setDateTo] = useState(fmt(today))
  const [activeReport, setActiveReport] = useState('occupancy')
  const [dropOpen, setDropOpen] = useState(false)
  const [availableYears, setAvailableYears] = useState([])
  const [selectedYear, setSelectedYear] = useState(today.getFullYear())
  const [onboardingDate, setOnboardingDate] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const dropRef = useRef(null)

  useEffect(() => {
    const handler = e => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // ─── Fetch onboarding date and set up dates ──────────────────────────────

  useEffect(() => {
    if (!selectedPGId) return

    setIsLoading(true)
    
    // Fetch occupancy report to get PGOwnBoardingDate
    // We'll use a default date range to get the onboarding info
    const tempFrom = fmt(new Date(today.getFullYear(), 0, 1)) // Jan 1 of current year
    const tempTo = fmt(today)

    getOccupancyReport(selectedPGId, tempFrom, tempTo)
      .then(response => {
        let onboardingDateStr = null
        
        // Extract PGOwnBoardingDate from response
        if (Array.isArray(response) && response.length >= 3) {
          const overallStats = response[2]?.[0]
          if (overallStats?.PGOwnBoardingDate) {
            onboardingDateStr = overallStats.PGOwnBoardingDate
          }
        } else if (response?.data && Array.isArray(response.data)) {
          const overallStats = response.data[2]?.[0]
          if (overallStats?.PGOwnBoardingDate) {
            onboardingDateStr = overallStats.PGOwnBoardingDate
          }
        }

        if (onboardingDateStr) {
          const onboarding = new Date(onboardingDateStr)
          setOnboardingDate(onboarding)
          
          // Set from date to onboarding date
          setDateFrom(fmt(onboarding))
          
          // Set to date to today
          setDateTo(fmt(today))
          
          // Generate available years (from onboarding year to current year, max 3 years)
          const onboardingYear = onboarding.getFullYear()
          const currentYear = today.getFullYear()
          const years = []
          
          // Start from onboarding year, go up to current year (max 3 years)
          for (let year = onboardingYear; year <= currentYear && year <= onboardingYear + 2; year++) {
            years.push(year)
          }
          
          // If current year is more than 2 years ahead, show only last 3 years
          if (years.length > 3) {
            years.splice(0, years.length - 3)
          }
          
          setAvailableYears(years)
          
          // Set selected year to current year if available, else latest
          if (years.includes(currentYear)) {
            setSelectedYear(currentYear)
          } else {
            setSelectedYear(years[years.length - 1] || currentYear)
          }
        } else {
          // Fallback: No onboarding date found, use default
          const currentYear = today.getFullYear()
          const years = [currentYear - 2, currentYear - 1, currentYear].filter(y => y >= 2020)
          setAvailableYears(years)
          setSelectedYear(currentYear)
          setDateFrom(fmt(new Date(currentYear, 0, 1)))
          setDateTo(fmt(today))
        }
      })
      .catch(err => {
        console.error('Error fetching onboarding date:', err)
        // Fallback on error
        const currentYear = today.getFullYear()
        const years = [currentYear - 2, currentYear - 1, currentYear].filter(y => y >= 2020)
        setAvailableYears(years)
        setSelectedYear(currentYear)
        setDateFrom(fmt(new Date(currentYear, 0, 1)))
        setDateTo(fmt(today))
      })
      .finally(() => setIsLoading(false))
  }, [selectedPGId])

  // ─── Validate date changes ────────────────────────────────────────────────

  const handleDateFromChange = (value) => {
    const newDate = new Date(value)
    if (onboardingDate && newDate < onboardingDate) {
      alert(`From date cannot be before PG onboarding date (${fmt(onboardingDate)})`)
      return
    }
    setDateFrom(value)
  }

  const handleDateToChange = (value) => {
    const newDate = new Date(value)
    const fromDate = new Date(dateFrom)
    if (newDate < fromDate) {
      alert('To date cannot be before from date')
      return
    }
    if (newDate > today) {
      alert('To date cannot be in the future')
      return
    }
    setDateTo(value)
  }

  const activeLabel = REPORT_TYPES.find(r => r.key === activeReport)?.label || 'Select Report'

  if (isLoading) {
    return <div className="rpt-loading">Loading report data...</div>
  }

  return (
    <div className="rpt-root">

      {/* ── Top bar ── */}
      <div className="rpt-topbar">
        <div className="rpt-topbar-left">
          <div className="rpt-date-group">
            <label className="rpt-date-label">From</label>
            <input 
              type="date" 
              className="rpt-date-input" 
              value={dateFrom} 
              max={dateTo} 
              min={onboardingDate ? fmt(onboardingDate) : undefined}
              onChange={e => handleDateFromChange(e.target.value)} 
            />
          </div>
          <span className="rpt-date-sep">→</span>
          <div className="rpt-date-group">
            <label className="rpt-date-label">To</label>
            <input 
              type="date" 
              className="rpt-date-input" 
              value={dateTo} 
              min={dateFrom} 
              max={fmt(today)} 
              onChange={e => handleDateToChange(e.target.value)} 
            />
          </div>
        </div>

        <div className="rpt-dropdown" ref={dropRef}>
          <button className="rpt-dropdown-btn" onClick={() => setDropOpen(o => !o)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 9h6M9 12h6M9 15h4" />
            </svg>
            {activeLabel}
            <svg className={`rpt-chevron ${dropOpen ? 'rpt-chevron-open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          {dropOpen && (
            <div className="rpt-dropdown-menu">
              {REPORT_TYPES.map(r => (
                <button
                  key={r.key}
                  className={`rpt-dropdown-item ${activeReport === r.key ? 'rpt-dropdown-item-active' : ''}`}
                  onClick={() => { setActiveReport(r.key); setDropOpen(false) }}
                >
                  {r.label}
                  {activeReport === r.key && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Header + year pills ── */}
      <div className="rpt-header">
        <div>
          <h2 className="rpt-title">{activeLabel}</h2>
          <p className="rpt-subtitle">
            {selectedPG} · {new Date(dateFrom).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })} – {new Date(dateTo).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}
          </p>
        </div>
        {availableYears.length > 0 && (
          <div className="rpt-year-filter">
            {availableYears.map(year => (
              <button 
                key={year} 
                className={`rpt-year-btn ${selectedYear === year ? 'active' : ''}`} 
                onClick={() => setSelectedYear(year)}
              >
                {year}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Active report ── */}
      {activeReport === 'occupancy' && (
        <OwnerOccupancyReport
          selectedPGId={selectedPGId}
          selectedPG={selectedPG}
          dateFrom={dateFrom}
          dateTo={dateTo}
          selectedYear={selectedYear}
        />
      )}
      {activeReport === 'complaints' && (
        <OwnerComplaintsReport
          selectedPGId={selectedPGId}
          selectedPG={selectedPG}
          selectedYear={String(selectedYear)}
        />
      )}
      {activeReport === 'revenue' && (
        <OwnerRevenueReport 
          selectedPGId={selectedPGId}
          selectedPG={selectedPG}
          selectedYear={selectedYear}
          dateFrom={dateFrom}
          dateTo={dateTo}
        />
      )}
      {activeReport === 'compliance' && <OwnerComplianceReport selectedPGId={selectedPGId} selectedPG={selectedPG} selectedYear={selectedYear} />}

    </div>
  )
}

export default OwnerReports